/**
 * 房间选择模块
 */
import React, { useEffect, useMemo, useRef, useState, memo } from 'react';
import { connect } from 'react-redux';
import { Checkbox, Tooltip, Input, message } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { SearchOutlined } from '@ant-design/icons';
import intl from 'react-intl-universal';

import { OccType, CustomUnableBookStatus, EnableBookEnum } from '@/config/constant';
import IconFont from '@/components/IconFont';
import {
  createStateSelector,
  filterArrByKey,
  findItemByKey,
} from '@/redux/actions/reservation/helper';
import {
  handleAllRoomSelect,
  handleCategorySelect,
  handleRoomSelect,
  handleAddBlockedRoom,
} from '@/redux/actions/reservation/createReserve';
import { RoomCategory, RoomItem } from '@/redux/reducers/reservation/base.d';
import { ReducerState, storeName } from '@/redux/reducers/reservation/createReserve';

import ListCollapse from './ListCollapse';
import styles from './index.module.scss';

const stateSelector = (state: ReducerState) => {
  const { allRoomList, selectRoomList, commonInfo, dayTypeTimeInfo, hourTypeTimeInfo } = state;
  const rentTimeInfo = commonInfo?.occType === OccType.DayUse ? dayTypeTimeInfo : hourTypeTimeInfo;
  return { allRoomList, selectRoomList, businessDate: rentTimeInfo.businessDate };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  callAllRoomSelect: handleAllRoomSelect,
  callCategorySelect: handleCategorySelect,
  callRoomSelect: handleRoomSelect,
  callAddBlockedRoom: handleAddBlockedRoom,
};
const LimitHint = intl.get('reservation_room_message_over_limit');

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

const Index: React.FC<MapStateProps & MapDispatchProps> = (props) => {
  const {
    allRoomList,
    selectRoomList,
    businessDate,
    callAllRoomSelect,
    callCategorySelect,
    callRoomSelect,
    callAddBlockedRoom,
  } = props;
  const searchValueRef = useRef<string>('');
  const [filterList, setFilterList] = useState<RoomCategory[]>([]);
  const isSearchResult = !!searchValueRef.current.length; // 是否是筛选

  const roomData = useMemo(() => {
    const roomIdMap: { [roomId: number]: RoomItem } = {}; // 房间map
    let totalCount = 0; // 房间总数量
    let validCount = 0; // 可选房间数量
    const defaultOpenIds: number[] = []; // 默认展开的房型id
    (allRoomList || []).forEach((category) => {
      const subList = category.roomList || [];
      const itemTotal = category.roomsTotal || 0;
      totalCount += itemTotal;
      subList.forEach((item) => {
        roomIdMap[item.id] = item;
        // v2.21 去除掉不可预订房间
        if (item?.bookingInfo?.enableBook !== EnableBookEnum.unableBook) {
          validCount += 1;
        }
      });
      if (itemTotal && !defaultOpenIds.length) {
        defaultOpenIds.push(category.id);
      }
    });
    return {
      roomIdMap,
      totalCount,
      validCount,
      defaultOpenIds,
    };
  }, [allRoomList]);

  // 选择的数据
  const selectData = useMemo(() => {
    let checkedRoomCount = 0; // 选中的房间数量
    let blockedRoomCount = 0; // 待排的房间数量
    (selectRoomList || []).forEach((item) => {
      if (item.roomId) {
        checkedRoomCount += 1;
      } else {
        blockedRoomCount += 1;
      }
    });
    const { validCount } = roomData;
    return {
      checkedRoomCount,
      blockedRoomCount,
      isAllRoomChecked: validCount && checkedRoomCount >= validCount,
    };
  }, [roomData, selectRoomList]);

  // 全选
  const handleAllChange: (event: CheckboxChangeEvent) => void = (event) => {
    callAllRoomSelect(event.target.checked);
  };

  // 切换单个选中状态
  const handleToggleItem: (item: RoomItem, checked: boolean) => void = (item, checked) => {
    if (checked) {
      const { roomTypeId } = item;
      const category = findItemByKey(allRoomList, 'id', roomTypeId); // 所属房型
      const roomList = category?.roomList || []; // 房间列表
      const selectList = filterArrByKey(selectRoomList, 'roomTypeId', roomTypeId); // 当前房型已选列表
      if (roomList.length <= selectList.length) {
        // 当前房型所属分类下房间数量 小于等于 当前房型已选数量
        message.error(LimitHint);
        return;
      }
    }
    callRoomSelect(item, checked);
  };

  // 添加待排房
  const handleBlockAdd: (item: RoomCategory, count: number) => void = (item, count) => {
    const roomTypeId = item.id;
    const category = findItemByKey(allRoomList, 'id', roomTypeId); // 所属房型
    const roomList = category?.roomList || []; // 房间列表
    const selectList = filterArrByKey(selectRoomList, 'roomTypeId', roomTypeId); // 当前房型已选列表
    if (selectList.length + count > roomList.length) {
      // 当前房型已选数量 + 新增待排房数量 大于等于 当前房型所属分类下房间数量
      message.error(LimitHint);
      return;
    }
    callAddBlockedRoom(roomTypeId, count);
  };

  // 切换房型选中状态
  const handleToggleCategory: (item: RoomCategory, checked: boolean) => void = (item, checked) => {
    callCategorySelect(item.id, checked);
  };

  const getSearchResult = (searchValue) => {
    const result: Array<RoomCategory> = [];
    if (searchValue) {
      const searchReg = new RegExp(searchValue);
      allRoomList.forEach((item) => {
        const filterRooms = filterArrByKey(item.roomList || [], (room) =>
          searchReg.test(room.roomNo),
        );
        if (filterRooms.length) {
          result.push({
            ...item,
            roomList: filterRooms,
          });
        }
      });
    }
    setFilterList(result);
  };

  const handleRoomSearch: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const searchValue = event.target.value;
    searchValueRef.current = searchValue;
    getSearchResult(searchValue);
  };

  useEffect(() => {
    if (searchValueRef.current) {
      getSearchResult(searchValueRef.current);
    }
  }, [allRoomList]);

  const { totalCount, validCount, defaultOpenIds } = roomData;
  const { isAllRoomChecked, checkedRoomCount } = selectData;

  const collapseConfig = useMemo(() => {
    const collapseOpenKey = isSearchResult ? filterList.map((item) => item.id) : defaultOpenIds; // 筛选默认展开全部
    return {
      indexId: collapseOpenKey.join('-'),
      collapseOpenKey,
    };
  }, [roomData.defaultOpenIds, filterList]);

  const isAllDisabled = isSearchResult || !validCount; // 筛选 或者没数据

  return (
    <div className={styles.roomListContainer}>
      <div className={styles.listHeader}>
        <div>
          <Checkbox
            checked={isAllRoomChecked}
            disabled={isAllDisabled}
            indeterminate={checkedRoomCount && !isAllRoomChecked}
            onChange={handleAllChange}
          >
            {intl.get('reservation_room_all_checkbox')}
          </Checkbox>
        </div>
        <div>
          <span className={styles.countIndicator}>{`${validCount}/${totalCount}`}</span>
          <Tooltip
            placement="top"
            title={intl.get('reservation_room_count_help', {
              total: totalCount,
              valid: validCount,
            })}
          >
            <IconFont type="icon-tishi" className={styles.countTipIcon} />
          </Tooltip>
        </div>
      </div>
      <div className={styles.listBody}>
        <div className={styles.searchWrapper}>
          <Input
            prefix={<SearchOutlined />}
            placeholder={intl.get('reservation_room_search_placeholder')}
            allowClear
            onChange={handleRoomSearch}
          />
        </div>
        <ListCollapse
          key={collapseConfig.indexId}
          isCategoryDisabled={isSearchResult}
          defaultOpenKey={collapseConfig.collapseOpenKey}
          selectRoomList={selectRoomList}
          dataSource={isSearchResult ? filterList : allRoomList}
          onItemSelect={handleToggleItem}
          onCategorySelect={handleToggleCategory}
          onBlockSelect={handleBlockAdd}
          businessDate={businessDate}
        />
      </div>
    </div>
  );
};

export default connect<MapStateProps, MapDispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(memo(Index));
